import { EditableItemConfiguration, EditableItemType, InputConfiguration } from '../../shared';
import { translationKeys } from '../translations';
import { DossierType, RequestedType, SubmissionStatus } from '../models';
import { typeField } from '@avano/avano-components/utils';
import { OrganizationDto, OrganizationGroupDto } from '@scip/api';
import { DossierUtils } from './dossier.utils';

export class SearchUtils {
  static createLabelledInputSearchConfig(label: string): EditableItemConfiguration {
    return {
      type: EditableItemType.INPUT,
      header: label,
      content: ''
    };
  }

  static createFromToDatePickerConfig(label: string): EditableItemConfiguration {
    return {
      type: EditableItemType.DATE_FROM_TO,
      header: label,
      placeholderFrom: translationKeys.search.service.datePicker.from,
      placeholderTo: translationKeys.search.service.datePicker.to,
      selection: []
    };
  }

  static createIncludeChildrenSearchConfig(): EditableItemConfiguration {
    const checkboxConfig: InputConfiguration[] = [];
    const includeChildren: InputConfiguration = {
      label: translationKeys.search.service.showChildren,
      value: 'SHOW_CHILDREN',
      selectedValue: false,
      isSelectedByDefault: false,
      disabled: false
    };

    checkboxConfig.push(includeChildren);

    return {
      type: EditableItemType.CHECKBOXES,
      header: '',
      inputs: checkboxConfig
    };
  }

  static createUploadTypeSearchConfig(): EditableItemConfiguration {
    const radioButtonsConfig: InputConfiguration[] = [];
    const all = {
      label: translationKeys.search.service.uploadType.ALL,
      value: 'ALL',
      isSelectedByDefault: true
    };
    const full = {
      label: translationKeys.dossierDto.requestedType.STANDARD,
      value: RequestedType.STANDARD
    };
    const fullReferencing = {
      label: translationKeys.dossierDto.requestedType.REFERENCING,
      value: RequestedType.REFERENCING
    };
    const partsOnly = {
      label: translationKeys.dossierDto.requestedType.PARTS_ONLY,
      value: RequestedType.PARTS_ONLY
    };
    radioButtonsConfig.push(all);
    radioButtonsConfig.push(full);
    radioButtonsConfig.push(fullReferencing);
    radioButtonsConfig.push(partsOnly);

    return {
      type: EditableItemType.RADIOBUTTONS,
      header: translationKeys.search.service.uploadTypes,
      content: radioButtonsConfig[0],
      inputs: radioButtonsConfig
    };
  }

  static createEchaTypeSearchConfig(): EditableItemConfiguration {
    const radioButtonsConfig: InputConfiguration[] = [];
    const all = {
      label: translationKeys.search.service.echaDossier.ALL,
      value: 'ALL',
      selectedValue: null,
      isSelectedByDefault: true
    };
    const full = {
      label: translationKeys.dossierDto.type.FULL,
      value: DossierType.FULL,
      selectedValue: null
    };
    const simplified = {
      label: translationKeys.dossierDto.type.SIMPLIFIED,
      value: DossierType.SIMPLIFIED,
      selectedValue: null
    };
    radioButtonsConfig.push(all);
    radioButtonsConfig.push(full);
    radioButtonsConfig.push(simplified);

    return {
      type: EditableItemType.RADIOBUTTONS,
      header: translationKeys.search.service.echaDossier.header,
      content: radioButtonsConfig[0],
      inputs: radioButtonsConfig
    };
  }

  static createLegalEntityGroupSearchConfig(): EditableItemConfiguration {
    return {
      type: EditableItemType.MULTISELECT,
      header: translationKeys.search.service.legalEntityGroup,
      suggestions: [], // Needs to be set by the component using this method
      field: typeField<OrganizationGroupDto>('name'),
      selection: []
    };
  }

  static createOrganizationMultiSelectSearchConfig(): EditableItemConfiguration {
    return {
      type: EditableItemType.MULTISELECT,
      header: translationKeys.search.service.organization,
      suggestions: [], // Needs to be set by the component using this method
      field: typeField<OrganizationDto>('name'),
      selection: []
    };
  }

  static createSubmissionStatusSearchConfig(): EditableItemConfiguration {
    const submissionStatuses: { name: string; value: string }[] = [
      SubmissionStatus.DOSSIER_REQUESTED,
      SubmissionStatus.DOSSIER_FILE_IN_PREPARATION,
      SubmissionStatus.PENDING,
      SubmissionStatus.DOSSIER_FAILED_TO_CREATE,
      SubmissionStatus.DOSSIER_FILE_CREATED,
      SubmissionStatus.SUBMITTED,
      SubmissionStatus.VALIDATION_SUCCEEDED,
      SubmissionStatus.FAILED,
      SubmissionStatus.VALIDATION_FAILED,
      SubmissionStatus.ALL_PARTS_COMPLETED,
      SubmissionStatus.ONE_OR_MORE_FAILED,
      SubmissionStatus.DOSSIER_SENT_TO_ECHA,
      SubmissionStatus.DOSSIER_TOO_LARGE
    ]
      .map((status: SubmissionStatus) => {
        return {
          name: DossierUtils.getTranslationKey(status),
          value: status
        };
      })
      // We currently sort according to the translation key, not the real translation
      // Does this make sense? No idea, there was no order defined.
      .sort((a, b) => (a.name > b.name ? 1 : b.name > a.name ? -1 : 0));

    return {
      type: EditableItemType.MULTISELECT,
      header: translationKeys.search.service.submissionStatus.submissionStatus,
      suggestions: submissionStatuses,
      suggestionsTranslate: true,
      field: 'name',
      selection: []
    };
  }

  static createIncludeLoadingStateSearchConfig(): EditableItemConfiguration {
    const checkboxConfig: InputConfiguration[] = [];
    const includeChildren: InputConfiguration = {
      label: translationKeys.search.service.productLoadingState,
      value: 'SHOW_LOADING_STATE',
      selectedValue: false,
      isSelectedByDefault: false,
      disabled: false
    };

    checkboxConfig.push(includeChildren);

    return {
      type: EditableItemType.CHECKBOXES,
      header: '',
      inputs: checkboxConfig
    };
  }
}
